import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { authApi, ApiError } from '../services/api';
import './EmailVerificationModal.css';

interface EmailVerificationModalProps {
  onClose: () => void;
  onSwitchToLogin: () => void;
  email: string;
}

interface FormErrors {
  code?: string;
  general?: string;
}

const EmailVerificationModal: React.FC<EmailVerificationModalProps> = ({ onClose, onSwitchToLogin, email }) => {
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    // Allow only digits and max length 6
    if (/^\d{0,6}$/.test(value)) {
      setCode(value);
      if (errors.code) {
        setErrors(prev => ({ ...prev, code: undefined }));
      }
    }
  };

  const handleBlur = () => {
    setTouched(prev => ({ ...prev, code: true }));
    validateField();
  };

  const validateField = (): boolean => {
    let isValid = true;
    const newErrors = { ...errors };

    if (code.length !== 6) {
      newErrors.code = 'Please enter a 6-digit verification code';
      isValid = false;
    } else {
      delete newErrors.code;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateField()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await authApi.verifyEmail({ email, otp: code });
      console.log('Verification successful!');
      onSwitchToLogin();
    } catch (error) {
      const apiError = error as { response?: { data?: ApiError } };
      if (apiError.response?.data) {
        const errorData = apiError.response.data;
        setErrors({ general: errorData.message || 'Invalid verification code. Please try again.' });
      } else {
        setErrors({ general: 'Verification failed. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Resend verification code function
  const resendVerificationCode = async () => {
    setIsResending(true);
    setErrors({});

    try {
      await authApi.resendVerificationCode({ email });
      console.log('Verification code resent to:', email);
    } catch (error) {
      const apiError = error as { response?: { data?: ApiError } };
      if (apiError.response?.data) {
        const errorData = apiError.response.data;
        setErrors({ general: errorData.message || 'Failed to resend verification code. Please try again.' });
      } else {
        setErrors({ general: 'Failed to resend verification code. Please try again.' });
      }
    } finally {
      setIsResending(false);
    }
  };

  const getInputClassName = (): string => {
    const hasError = errors.code;
    const isTouched = touched.code;

    if (isTouched && hasError) {
      return 'error';
    }
    return '';
  };

  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot) return null;

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Email Verification</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form" noValidate>
          <p>Please enter the 6-digit verification code sent to <strong>{email}</strong>.</p>

          {errors.general && (
            <div className="error-message general-error">{errors.general}</div>
          )}

          <div className="form-group">
            <label htmlFor="verification-code">Verification Code</label>
            <input
              type="text"
              id="verification-code"
              value={code}
              onChange={handleChange}
              onBlur={handleBlur}
              className={getInputClassName()}
              placeholder="Enter 6-digit code"
              maxLength={6}
              required
            />
            {touched.code && errors.code && (
              <span className="field-error">{errors.code}</span>
            )}
          </div>

          <button
            type="submit"
            className="btn-primary"
            disabled={isLoading}
          >
            {isLoading ? 'Verifying...' : 'Verify'}
          </button>

          <p className="resend-code">
            Didn't receive the code?{' '}
            <button
              type="button"
              className="link-btn"
              onClick={resendVerificationCode}
              disabled={isResending}
            >
              {isResending ? 'Resending...' : 'Resend Code'}
            </button>
          </p>
        </form>
      </div>
    </div>,
    modalRoot
  );
};

export default EmailVerificationModal;
